import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Link, LinearProgress, Grid2 } from "@mui/material";
import CustomIcon from "../../../Components/CustomIcon";
import { PrimaryFormLoadingActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { handleFileUpload } from "../DataUploadFunctions";

const Step2FileUpload = ({ selectedResourceType, onFileSelect, onNext }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [csvPreviewData, setCsvPreviewData] = useState(null);
    const fileInputRef = useRef(null);
    const progressIntervalRef = useRef(null);

    // Get the CSV template path and label from selectedResourceType
    const csvTemplatePath = selectedResourceType?.sampleCSVTemplateS3Path;
    const resourceTypeLabel = selectedResourceType?.label || 'Resource Onboarding';

    const validateFile = (file) => {
        // Check file type
        if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
            alert('Please upload a CSV file only');
            return false;
        }

        // Check file size (25 MB = 25 * 1024 * 1024 bytes)
        const maxSize = 25 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('File size exceeds 25 MB limit');
            return false;
        }

        return true;
    };

    const simulateUpload = (file) => {
        setIsUploading(true);
        setUploadProgress(0);
        setUploadComplete(false);

        // Simulate upload progress
        progressIntervalRef.current = setInterval(() => {
            setUploadProgress((prevProgress) => {
                if (prevProgress >= 100) {
                    clearInterval(progressIntervalRef.current);
                    setIsUploading(false);
                    setUploadComplete(true);
                    return 100;
                }
                // Increase by random amount between 5-15%
                return Math.min(prevProgress + Math.random() * 10 + 5, 100);
            });
        }, 200);

        /*
        // TODO: Replace with actual API call
        const uploadFile = async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('resourceType', selectedResourceType);

            try {
                const response = await fetch('/api/upload-resource', {
                    method: 'POST',
                    body: formData,
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setUploadComplete(true);
                    setIsUploading(false);
                } else {
                    throw new Error('Upload failed');
                }
            } catch (error) {
                console.error('Upload error:', error);
                setIsUploading(false);
                alert('Failed to upload file. Please try again.');
            }
        };

        uploadFile(file);
        */
    };

    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (file && validateFile(file)) {
            // Parse CSV for preview and validation
            try {
                const result = await handleFileUpload(file);

                // Validate row count (max 500 data rows)
                if (result.totalRows > 500) {
                    alert(`CSV file contains ${result.totalRows} rows. Maximum allowed is 500 rows (excluding header).`);
                    // Reset file input
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                    return;
                }

                setSelectedFile(file);
                onFileSelect(file);
                setCsvPreviewData(result);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                alert(error.message);
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            simulateUpload(file);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (file && validateFile(file)) {
            // Parse CSV for preview and validation
            try {
                const result = await handleFileUpload(file);

                // Validate row count (max 500 data rows)
                if (result.totalRows > 500) {
                    alert(`CSV file contains ${result.totalRows} rows. Maximum allowed is 500 rows (excluding header).`);
                    return;
                }

                setSelectedFile(file);
                onFileSelect(file);
                setCsvPreviewData(result);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                alert(error.message);
                return;
            }

            simulateUpload(file);
        }
    };

    const handleSelectFileClick = () => {
        fileInputRef.current?.click();
    };

    const handlePreview = () => {
        if (selectedFile && csvPreviewData && onNext) {
            onNext(selectedFile, csvPreviewData);
        }
    };

    return (
        <Box>
            {/* Header with title and download link */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5 }}>
                <Typography variant="fw500s20c000000" sx={{ lineHeight: '20px' }}>
                    {resourceTypeLabel}
                </Typography>
                {csvTemplatePath && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="fw500s14c000000" sx={{ color: '#686868' }}>
                            Need a CSV Template?
                        </Typography>
                        <Link
                            href={csvTemplatePath}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{ cursor: 'pointer'}}
                        >
                            <Typography variant="fw500s14c000000" sx={{ color: '#6D72F6' }}>
                                Download Here
                            </Typography>
                        </Link>
                    </Box>
                )}
            </Box>

            {/* Upload instructions */}
            <Box sx={{ mb: 1 }}>
                <Typography variant="fw400s14lh24c686868">
                    Upload CSV file with up to 500 records only.
                </Typography>
            </Box>


            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Drop zone */}
            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleSelectFileClick}
                sx={{
                    border: `1px ${selectedFile ? 'solid' : 'dashed'} ${isDragging ? '#6D72F6' : '#C8D1DA'}`,
                    borderRadius: '16px',
                    padding: selectedFile ? '16px 24px' : '40px',
                    textAlign: 'center',
                    backgroundColor: isDragging ? '#F8F8FE' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: '#6D72F6',
                        backgroundColor: '#F8F8FE'
                    }
                }}
            >
                {selectedFile ? (
                    <Grid2 container justifyContent={'space-between'} alignItems={'flex-start'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <CustomIcon name="csv-icon" width="37px" height="37px" />
                            <Grid2 container flexDirection={'column'} alignItems={'flex-start'} gap={0.25}>
                                <Typography variant="fw500s16c000000" sx={{ lineHeight: '24px', color: '#101111' }}>
                                    {selectedFile.name}
                                </Typography>
                                <Typography variant="fw400s14lh20c2E2E2E" sx={{ lineHeight: '21px', color: '#949FB1' }}>
                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                </Typography>
                            </Grid2>
                        </Box>

                        {isUploading && (
                            <Box sx={{ width: '100%', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{width: 'calc(100% - 30px)'}}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadProgress}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: '#E0E0E0',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#6D72F6',
                                                borderRadius: 3
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{width: '30px'}}>
                                    <Typography variant="fw400s12c686868" sx={{ lineHeight: '16px', color: '#6D6D6D' }}>
                                        {Math.round(uploadProgress)}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {!isUploading && (
                            <Button
                                variant="text"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectFileClick();
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#6D72F6',
                                    p: 0,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Change File
                            </Button>
                        )}
                    </Grid2>
                ) : (
                    <>
                        {/* <InsertDriveFileIcon sx={{ fontSize: 48, color: '#BDBDBD', mb: 2 }} /> */}
                        <Box sx={{mb: 3.5, display: 'flex', justifyContent: 'center'}}>
                            <CustomIcon name="csv-icon" width="37px" height="37px" />

                        </Box>
                        <Grid2 container flexDirection={'column'} gap={1.5} mb={2}>
                            <Typography variant="fw500s20c000000" sx={{ fontSize: '18px', color: '#101111' }}>
                                Drop your CSV file here
                            </Typography>
                            <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '20px' }}>
                                Supported format: CSV format <br />
                                Maximum file size: 25 MB
                            </Typography>
                        </Grid2>
                        <Button
                            variant="text"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectFileClick();
                            }}
                            sx={{
                                textTransform: 'none',
                                fontSize: '16px',
                                lineHeight: '16px',
                                fontWeight: 500,
                                color: '#6D72F6',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                }
                            }}
                        >
                            Select File
                        </Button>
                    </>
                )}
            </Box>

            {/* Preview button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <PrimaryFormLoadingActionButton
                    disabled={!uploadComplete}
                    onClick={handlePreview}
                    loading={false}
                    sx={{ borderRadius: '10px', p: 2 }}
                >
                    Preview
                </PrimaryFormLoadingActionButton>
            </Box>
        </Box>
    );
};

export default Step2FileUpload;
